import classNames from 'classnames';
import * as React from 'react';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ContentListItem } from '~/Partials/ContentListItem/ContentListItem';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import { List } from '~/Partials/List/List';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { replaceNotFriendlyCharacters } from '~/Partials/Utils/utils';
import { BackgroundVariant } from './BackgroundVariant.csharp';
import { ColumnCountVariant } from './ColumnCountVariant.csharp';
import styles from './ContentList.module.css';
import { ContentListLayoutVariant } from './ContentListLayoutVariant.csharp';
import type { ContentListBlockViewModel } from './ViewModel/ContentListBlockViewModel.csharp';

export interface ContentListProps extends ContentListBlockViewModel {
    linkSize?: ButtonSize;
    withWrapper?: boolean;
    className?: string;
}

export const ContentList: React.FC<React.PropsWithChildren<ContentListProps>> = props => {
    const {
        title,
        link,
        contentListItems,
        layoutVariant,
        backgroundVariant,
        columnVariant,
        className,
        linkSize = ButtonSize.Large,
    } = props;

    const getContentListItemType = () =>
        columnVariant === ColumnCountVariant.OneTopAndThreeBelow ? 'verticalNoImage' : 'vertical';

    const mainItem = columnVariant === ColumnCountVariant.OneTopAndThreeBelow && contentListItems[0];

    const listItems =
        columnVariant === ColumnCountVariant.OneTopAndThreeBelow && contentListItems.length > 0
            ? contentListItems.slice(1)
            : contentListItems;

    const getLinkButtonTier = () => {
        switch (backgroundVariant) {
            case BackgroundVariant.Red:
            case BackgroundVariant.Dark:
                return ButtonTier.Tertiary;
            default:
                return ButtonTier.Primary;
        }
    };

    const renderLink = (withIcon: boolean) => (
        <Link
            {...link}
            showIsExternal={false}
            type={LinkType.Button}
            buttonTier={getLinkButtonTier()}
            buttonSize={linkSize}
        >
            <span>{link.label}</span>
            {withIcon && <SvgIcon iconName="arrow-right" />}
        </Link>
    );

    const isLinkValid = link && link.href;
    const shouldRenderLinkOnTop = layoutVariant === ContentListLayoutVariant.TitleAndLinkTop && isLinkValid;
    const shouldRenderLinkOnBottom = layoutVariant === ContentListLayoutVariant.TitleTopLinkBottom && isLinkValid;
    const id = title ? replaceNotFriendlyCharacters(title.toLowerCase()) : undefined;

    return (
        <div
            className={classNames(
                styles.ContentList,
                styles[`Variant--${backgroundVariant}`],
                styles[`Variant--${layoutVariant}`],
                className
            )}
        >
            <div className={styles.Inner} id={id}>
                <div className={styles.Header}>
                    {title && <h2 className={styles.Title}>{title}</h2>}
                    {shouldRenderLinkOnTop && renderLink(true)}
                </div>
                {contentListItems.length > 0 && (
                    <div className={classNames(styles.Body, styles[`Body--${columnVariant}ColumnVariant`])}>
                        {mainItem && (
                            <div className={styles.MainItem}>
                                <ContentListItem {...mainItem} itemType="horizontal" variant={backgroundVariant} />
                            </div>
                        )}
                        {listItems.length > 0 && (
                            <List
                                className={styles.Items}
                                items={listItems}
                                renderItem={item => (
                                    <li key={item.id}>
                                        <ContentListItem
                                            {...item}
                                            itemType={getContentListItemType()}
                                            variant={backgroundVariant}
                                        />
                                    </li>
                                )}
                            />
                        )}
                    </div>
                )}
                {shouldRenderLinkOnBottom && <div className={styles.Footer}>{renderLink(false)}</div>}
            </div>
        </div>
    );
};
